/* global module */
// SEO values shared by the app (PageSEO, Breadcrumbs) and the post-build step
// (scripts/generate-static-meta.js). Plain CommonJS on purpose, so Node can
// require() it without a bundler and webpack can still import it.

// Apex host: www.eattechpl.com 301-redirects here, so canonicals must use this origin.
const BASE_URL = 'https://eattechpl.com';
const SITE_NAME = 'E.A.T.';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`;

// Link-preview card for each page. Cards are generated by scripts/generate-og-images.js
// (npm run og); IdeaToImpact keeps its own hand-made card. Paths missing here use the default.
const OG_IMAGE_PATH = {
  '/about': '/og/about.jpg',
  '/services': '/og/services.jpg',
  '/program': '/og/program.jpg',
  '/ideatoimpact': '/og-ideatoimpact.png',
  '/ideatoimpact/apply': '/og-ideatoimpact.png',
  '/project-assistance': '/og/project-assistance.jpg',
  '/careers': '/og/careers.jpg',
  '/register': '/og/register.jpg',
  '/contact': '/og/contact.jpg',
  '/faq': '/og/faq.jpg',
  '/portfolio': '/og/portfolio.jpg',
  '/founder': '/og/founder.jpg',
  '/privacy': '/og/privacy.jpg',
};

// What the card shows, for og:image:alt.
const OG_IMAGE_ALT = {
  '/about': 'About E.A.T.: an IT company in Belgaum, Karnataka',
  '/services': 'E.A.T. services: software, web and mobile app development and digital marketing',
  '/program': 'CampusToCorp: IT internship and corporate readiness program, Belgaum and Bangalore',
  '/ideatoimpact': 'E.A.T. IdeaToImpact: project sponsorship and problem-solving program',
  '/ideatoimpact/apply': 'E.A.T. IdeaToImpact: project sponsorship and problem-solving program',
  '/project-assistance': 'E.A.T. Project Development Assistance for students',
  '/careers': 'Work with E.A.T.: current openings',
  '/register': 'Join CampusToCorp, a 6-month IT internship for final-year students',
  '/contact': 'Contact E.A.T. in Belgaum and Bangalore',
  '/faq': 'Frequently asked questions about the CampusToCorp program',
  '/portfolio': 'E.A.T. portfolio: clients we have delivered for and software built by our founder',
  '/founder': 'Furqan Mestri, Founder and CEO of E.A.T.',
  '/privacy': 'E.A.T. Privacy Policy',
};

// Breadcrumb name for each page, and its parent when it is not directly under Home.
const BREADCRUMBS = {
  '/about': { name: 'About E.A.T.' },
  '/founder': { name: 'Furqan Mestri', parent: '/about' },
  '/services': { name: 'Services' },
  '/portfolio': { name: 'Our Work' },
  '/program': { name: 'CampusToCorp' },
  '/register': { name: 'Enroll', parent: '/program' },
  '/faq': { name: 'FAQ', parent: '/program' },
  '/ideatoimpact': { name: 'IdeaToImpact' },
  '/ideatoimpact/apply': { name: 'Submit your idea', parent: '/ideatoimpact' },
  '/project-assistance': { name: 'Project Assistance' },
  '/careers': { name: 'Careers' },
  '/contact': { name: 'Contact' },
  '/privacy': { name: 'Privacy Policy' },
};

const CAREERS_PATH = '/careers';

// Job pages share the Careers card.
const cardKey = (path) => (path.startsWith(`${CAREERS_PATH}/`) ? CAREERS_PATH : path);

function ogImageFor(path) {
  const rel = OG_IMAGE_PATH[cardKey(path)];
  return rel ? `${BASE_URL}${rel}` : DEFAULT_IMAGE;
}

function ogImageAltFor(path) {
  return OG_IMAGE_ALT[cardKey(path)] || null;
}

// [{ name, path }] from Home down to the page. `leafName` names a dynamic page
// (a job under /careers) that has no entry of its own. Home has no trail.
function breadcrumbTrail(path, leafName) {
  if (path === '/') return [];
  const trail = [];
  let current = BREADCRUMBS[path];
  let currentPath = path;
  if (!current && leafName && path.startsWith(`${CAREERS_PATH}/`)) {
    trail.unshift({ name: leafName, path });
    currentPath = CAREERS_PATH;
    current = BREADCRUMBS[CAREERS_PATH];
  }
  while (current) {
    trail.unshift({ name: current.name, path: currentPath });
    currentPath = current.parent;
    current = currentPath ? BREADCRUMBS[currentPath] : null;
  }
  if (!trail.length) return [];
  return [{ name: 'Home', path: '/' }, ...trail];
}

function breadcrumbJsonLd(trail) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${BASE_URL}${item.path === '/' ? '/' : item.path}`,
    })),
  };
}

// The founder's profile, so a search for his name has a clear page to land on.
const FOUNDER_ID = `${BASE_URL}/founder#person`;
const FOUNDER_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  '@id': `${BASE_URL}/founder#profile`,
  url: `${BASE_URL}/founder`,
  name: 'Furqan Mestri: Founder & CEO of E.A.T.',
  mainEntity: {
    '@type': 'Person',
    '@id': FOUNDER_ID,
    name: 'Furqan Mestri',
    givenName: 'Furqan',
    familyName: 'Mestri',
    jobTitle: 'Founder & CEO',
    description:
      'Furqan Mestri is the founder and CEO of E.A.T., a Tech Lead, Consultant and Trainer with 11+ years in software engineering across India, USA, UAE, KSA, Mexico, Malawi and France.',
    url: `${BASE_URL}/founder`,
    image: `${BASE_URL}/furqan-mestri.jpg`,
    worksFor: { '@id': `${BASE_URL}/#organization` },
    homeLocation: { '@type': 'City', name: 'Belagavi', alternateName: 'Belgaum' },
    knowsAbout: [
      'Software engineering',
      'IT consulting',
      'Corporate training',
      'Agile delivery',
      'Software project management',
    ],
    sameAs: ['https://www.linkedin.com/in/furqan-mestri/'],
  },
};

module.exports = {
  BASE_URL,
  SITE_NAME,
  DEFAULT_IMAGE,
  OG_IMAGE_PATH,
  CAREERS_PATH,
  FOUNDER_JSONLD,
  ogImageFor,
  ogImageAltFor,
  breadcrumbTrail,
  breadcrumbJsonLd,
};
